import { FunctionComponent } from 'preact';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'preact/hooks';
import { Icon } from '../../../common/components/icon/icon';
import { getCtaArrowUpId, getCtaButtonId, getCtaDotId, getCtaStringId } from '../htmlElements/htmlElementsIdGetter';
import { CONTROLS_ICON_TYPES } from '../icon/iconMapper';
import { callToActionSelector } from '../../../common/store/selectors/callToAction.selector';
import { playerDataSelector } from '../../../common/store/selectors/playerData.selector';
import { adsDataSelector } from '../../../common/store/selectors/adsData.selector';
import { setUserInteraction } from '../store/controlsPlugin.actions';
import { getCtaButtonStyleObject } from './callToActionButton.style';
import { css } from '../../../common/styles/styles.utils';
import { isEmpty } from '../../../common/utils/utils';
import { splitViewSelector } from '../../../common/store/selectors/splitView.selector';
import { useHoverRef } from '../../../common/hooks/useHoverRef';
import { mediaDataSelector } from '../../../common/store/selectors/mediaData.selector';
import { moreVideosSelector } from '../../../common/store/selectors/moreVideos.selector';

const ARIA_LABEL = 'Call to action';

export const CallToActionButton: FunctionComponent = () => {
  const dispatch = useDispatch();
  const playerUniqueId = useSelector(playerDataSelector.playerInstanceUniqId);
  const loadingPlayer = useSelector(playerDataSelector.loadingPlayer);
  const videoTagStatus = useSelector(playerDataSelector.videoTagStatus);
  const playerSize = useSelector(playerDataSelector.playerSize);
  const playbackMethod = useSelector(playerDataSelector.playbackMethod);
  const adBreak = useSelector(adsDataSelector.adBreak);
  const buttonStringPerPlayer = useSelector(callToActionSelector.buttonStringPerPlayer);
  const urlPerPlayer = useSelector(callToActionSelector.urlPerPlayer);
  const buttonStringPerVideo = useSelector(callToActionSelector.buttonStringPerVideo);
  const urlPerVideo = useSelector(callToActionSelector.urlPerVideo);
  const splitViewRatio = useSelector(splitViewSelector.splitViewRatio);
  const videoList = useSelector(mediaDataSelector.videoList);
  const showGalleryView = useSelector(moreVideosSelector.showGalleryView);

  const [CtaURL, setCtaURL] = useState('');
  const [CtaButtonString, setCtaButtonString] = useState('');

  useEffect(() => {
    if (!isEmpty(urlPerVideo)) {
      setCtaURL(urlPerVideo);
      setCtaButtonString(buttonStringPerVideo);
    } else if (!isEmpty(urlPerPlayer)) {
      setCtaURL(urlPerPlayer);
      setCtaButtonString(buttonStringPerPlayer);
    }
  }, [buttonStringPerVideo, urlPerVideo, buttonStringPerPlayer, urlPerPlayer]);

  const isEndState = videoList.length === 1 && videoTagStatus === 'complete';
  const clickToPlayMode = videoTagStatus === 'idle' && playbackMethod === 'none';
  const contentIsPlaying = videoTagStatus === 'playing' || videoTagStatus === 'paused' || videoTagStatus === 'seeking';
  const noAds = !adBreak;
  const isSplitView = !isEmpty(splitViewRatio);
  const mediumSize = playerSize === 'medium';
  const moreVideosOpenOnMediumSize = mediumSize && showGalleryView;
  const playerSizeMediumAndAbove = playerSize !== 'small' && playerSize !== 'extraSmall';
  const showCta = noAds && !isSplitView && !isEndState && !loadingPlayer
    && playerSizeMediumAndAbove && (contentIsPlaying || clickToPlayMode) && !moreVideosOpenOnMediumSize;

  const [ctaButtonRef, ctaButtonHovered] = useHoverRef();

  const styles = getCtaButtonStyleObject(showCta, ctaButtonHovered, playerSize);
  const { arrowUpStyle, dotStyle, titleStyle, buttonStyle } = styles;

  const onCtaButtonClicked = (): void => {
    dispatch(setUserInteraction('callToActionButtonClicked'));
  };

  return (
    <a
      ref={ctaButtonRef}
      id={getCtaButtonId(playerUniqueId)}
      href={CtaURL}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onCtaButtonClicked}
      style={css(buttonStyle)}
    >
      <span id={getCtaDotId(playerUniqueId)} style={css(dotStyle)} />
      <span id={getCtaStringId(playerUniqueId)} style={css(titleStyle)}>
        {CtaButtonString}
      </span>
      <Icon
        id={getCtaArrowUpId(playerUniqueId)}
        icon={CONTROLS_ICON_TYPES.ARROW_UP_RIGHT}
        style={arrowUpStyle}
        ariaLabel={ARIA_LABEL}
      />
    </a>
  );
};
